import { createEffect, createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { type EnrollRefusal, enrollRefusal, shownRefusal } from '../lib/enroll-refusal';
import { type SshEnrollQueued, type SshEnrollRequest, sshEnroll } from '../lib/fleet';
import { createMutation } from '../lib/mutation';
import { SshEnrollForm } from './SshEnrollForm';

// SSH-pull transport for the Connect a machine dialog (#886, surfaced by #1035). The
// operator hands us the box coordinates and the PLATFORM dials in and runs the
// join — the enrollment token goes straight from the mint to the bootstrap lane,
// so this path has no secret to reveal and none to lose.
//
// Container: it owns the one POST and the state around it; <SshEnrollForm/>
// renders. Two rules it exists to hold:
//
//   - A 202 is not an enrollment. The bootstrap runs out of band and its
//     outcome lands as a `runner.ssh_bootstrap_{succeeded,failed}` audit row, so
//     the panel says "queued" and never "connected".
//   - The refusal line is the SERVER's. `vetSshTarget` is the arbiter, the seat
//     cap carries its own numbers, and 503 names why ssh-pull is unavailable —
//     each problem `detail` is already the sentence to show, so it is rendered
//     verbatim and the per-status fallback only covers a body that never came.
//     The one framed refusal is the 402 whose `reason` is `org_unfunded`, read
//     off the response itself (`lib/enroll-refusal.ts`).

export type SshEnrollPanelProps = {
  /** The dialog's open state — a close re-arms the form (see below). */
  readonly open: boolean;
  readonly token: string | null;
  /** Called after a queued bootstrap so the parent can refetch the roster. */
  readonly onQueued: () => void;
  readonly onClose: () => void;
  /** As `EnrollTransportToken`'s: a plan refusal is lifted to the dialog shell's plan line. */
  readonly onRefusal?: ((refusal: EnrollRefusal | null) => void) | undefined;
};

export function SshEnrollPanel(props: SshEnrollPanelProps): JSX.Element {
  const [refusal, setRefusal] = createSignal<EnrollRefusal | null>(null);
  const [queued, setQueued] = createSignal<SshEnrollQueued | null>(null);

  // A close re-arms the panel, for the same reason the token transport clears
  // its plaintext: the dialog delays unmount for its exit animation, so a quick
  // reopen would otherwise show last visit's queued bootstrap as if it were
  // this one's.
  createEffect(() => {
    if (!props.open) {
      setQueued(null);
      setRefusal(null);
    }
  });

  // `refusalFor` reads the STATUS, not the sentence: a seat-cap 402 is a hard
  // stop while an unreachable-transport 503 is "try again in a moment", and the
  // panel renders them differently. That is why the kit hands `onError` the
  // rejection itself alongside the string it derived.
  const enroll = createMutation({
    call: (request: SshEnrollRequest) => sshEnroll(request, props.token),
    fallback: t('fleet.addbox.ssh_error'),
    onSuccess: (result) => {
      setRefusal(null);
      setQueued(result);
      props.onQueued();
    },
    onError: (_message, cause) => setRefusal(refusalFor(cause)),
  });
  createEffect(() => props.onRefusal?.(refusal()));
  const busy = enroll.pending;
  const submit = (request: SshEnrollRequest): void => {
    setRefusal(null);
    void enroll.run(request);
  };

  return (
    <SshEnrollForm
      open={props.open}
      busy={busy()}
      refusal={shownRefusal(refusal(), props.onRefusal !== undefined)}
      queued={queued()}
      onSubmit={submit}
      onClose={props.onClose}
    />
  );
}

/**
 * One legible line for a refused enroll, plus how hard it reads.
 *
 * `soft` is the 503 door, and it is a retryable one: no platform SSH key sealed
 * refuses before anything is minted, and a failed bootstrap enqueue now RELEASES
 * the seat its mint reserved before answering (#1101), so neither costs a box
 * slot. The server's own line says which happened — including the one case that
 * still holds a slot, a release that itself failed
 * (`apps/api/src/routes/runners.ts`) — so the fallback copy below only ever
 * covers a 503 that arrived with no body at all.
 */
function refusalFor(error: unknown): EnrollRefusal {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 402) return enrollRefusal(error, t('fleet.addbox.seat_cap'));
  if (status === 503) return enrollRefusal(error, t('fleet.addbox.ssh_unavailable'), true);
  return enrollRefusal(error, t('fleet.addbox.ssh_error'));
}
